import { DataStorageEnums, DataStorageQuery } from 'dashboard-services/api/dataStorage';
import { CurveKey, DataTransformQuery, GetCurveBody } from 'dashboard-services/api/dataTransform';
import Query from 'dashboard-services/api/Query';
import TimeService from 'dashboard-services/services/TimeService';

import getApiConfig from 'api/ApiConfig';
import FORMULAS from 'resources/constants/Formulas.js';

import ExcelUtils from './ExcelUtils';
import FormulaUtils from './FormulaUtils';
import { normalizeFormulaArgument, Parameters, splitFormulaArguments } from './ParameterBuilder';
import { throttledFetchNoMillisLimit } from './ThrottleUtils';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { parse } from 'papaparse';

export default(() => {
  const SYMBOL = "Symbol",
        PRODUCT = "Product",
        ROOT = "FuturesRoot",
        DATA_TYPE = "DataType",
        CC_ROOT = "CC Root",
        FC_PARAMS_PREFIX = "FC Parameters: "

  const PLAIN_PARSED_CURVE_PARAMS = ["range", "lastTypeAmount", "lastType", "fromSelected", "from", "toSelected", "to", "symbolSize", "csvHeaders", "dateFormat", "timeZone", "periodType", "contractSort", "fillBlanks", "isRelative"]

  const DEFAULT_CSV_HEADERS = [{
    name: "Description",
    type: DataStorageEnums.CSV_HEADER_TYPE.METADATA_KEY
  }, {
    name: ROOT,
    type: DataStorageEnums.CSV_HEADER_TYPE.METADATA_KEY
  }, {
    name: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_COLUMN,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }]

  const CSV_HEADER_OPTIONS = [{
    label: "Column",
    value: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_COLUMN,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }, {
    label: "Group",
    value: DataStorageEnums.CSV_HEADER_STATICS.GROUP_NAME,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }]

  const normalizeDateParam = ({ value, isEndDate = false }) => {
    if(FormulaUtils.isLikelyExcelDateSerial(Number(value))) {
      return FormulaUtils.excelSerialToDateString({ value, withTime: true, isEndDate });
    }
    return value
  }

  const getKeys = rawKeys => 
    rawKeys.map(key => 
      new CurveKey.Builder()
        .withRoot(key.root)
        .withGroupName(key.groupName)
        .withColumn(key.column)
        .build()
    )

  const getBody = ({ keys, symbolSize, contractSort, dateFormat, fillBlanks, periodType, isRelative, timeZone, startDate, endDate, range, csvHeaders, lastType, lastTypeAmount } = {}) => {
    let builder = new GetCurveBody.Builder()
                    .withKeys(getKeys(keys))
                    .withContractSort(contractSort)
                    .withDateFormat(dateFormat)
                    .withFillBlanks(fillBlanks)
                    .withPeriodType(periodType)
                    .withLimitContracts(symbolSize)
                    .withRelative(isRelative === "true")
                    .withTimezone(timeZone)
    if(range === "TODAY") {
      builder = builder
        .withStartDate(DataStorageEnums.DATE_BETWEEN_TYPE.TODAY)
        .withEndDate(DataStorageEnums.DATE_BETWEEN_TYPE.TODAY)
        .withRange(DataStorageEnums.DATA_RANGE.BETWEEN)
    } else if(range === DataStorageEnums.DATA_RANGE.BETWEEN) {
      builder = builder
              .withStartDate(startDate === "undefined" || startDate === "Invalid Date" ? undefined : normalizeDateParam({ value: startDate }))
              .withEndDate(endDate === "undefined" || endDate === "Invalid Date" ? undefined : normalizeDateParam({ value: endDate, isEndDate: true }))
              .withRange(range)
    } else if(range === DataStorageEnums.DATA_RANGE.LAST || range === DataStorageEnums.DATA_RANGE.LAST) {
      builder = builder
              .withLastType(lastType)
              .withLastTypeAmount(lastTypeAmount)
              .withRange(range)
    } else {
      builder = builder.withRange(range)
    }
    if(typeof csvHeaders === "object" && csvHeaders?.length > 0) {
      csvHeaders?.forEach(csvHeader => {
        if(csvHeader?.name && csvHeader?.name !== "SYMBOL_VALUES") {
          builder = builder.withCsvHeader(csvHeader)
        }
      })
    } else {
      DEFAULT_CSV_HEADERS.forEach(defaultCsvHeader => {
        builder = builder.withCsvHeader(defaultCsvHeader)
      })
    }
    return builder.build()
  }

  const getForwardCurve = async (params = {}) => async dispatch => {
    const body = getBody(params)
    const getQueryBuilder = () => 
      new Query.Builder(dispatch(getApiConfig()))
        .withHeader("Target", params.uniqueProviders[0], true)
    const rawResponse = await DataTransformQuery
      .QUERIES
      .getCurveData(getQueryBuilder, { ...body, useV2: true })
      .mutationFn()
    const textResponse = await rawResponse.text()
    if(!textResponse) {
      return textResponse;
    }
    const parsedResponse = 
        await new Promise((resolve, reject) =>
          parse(textResponse, {
            complete: result => {
              const newResult = result.data
              if(newResult[newResult.length - 1]?.length !== newResult[0]?.length) {
                newResult.pop()
              }
              return resolve(newResult)
            },
            error: err => {
              reject(err)
              console.error(String(err))
            }
          })
        )
    return parsedResponse;
  }

  const getForwardCurveDatapoint = ({ parsedParameters } = {}) => async dispatch => {
    dayjs.extend(utc)
    let localDate
    if (FormulaUtils.isLikelyExcelDateSerial(Number(parsedParameters.date))) {
      localDate = FormulaUtils.excelSerialToDateString({ value: parsedParameters.date, withTime: true })
    }
    const body = {
      date: dayjs.utc(localDate ? localDate : parsedParameters.date).format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE),
      groupName: parsedParameters.groupName,
      root: parsedParameters.root,
      column: parsedParameters.column,
      periodType: parsedParameters.periodType,
      relative: parsedParameters.relative,
      period: parsedParameters.period,
      source: parsedParameters.source
    }
    const getQueryBuilder = () => new Query.Builder(dispatch(getApiConfig({ fetchOverride: throttledFetchNoMillisLimit })))
    const response = await DataStorageQuery.QUERIES
      .getCurvePoint(getQueryBuilder, { body, acceptJson: false })
      .mutationFn()
    if (response) {
      return response
    }
  }

  const paramsToFormula = params => {
    const groupedRawParams = params.getGroupedRawParams?.() || []
    const groupedParams = params.getGroupedParams().map((item, index) => Object.entries(item).map(([k, v]) => (
      Object.hasOwn(groupedRawParams?.[index] || {}, k) ? groupedRawParams[index][k] : `"${k}=${v}"`
    )).join(", ")).join(", ")
    const regularRawParams = params.getRawParameters?.() || {}
    const regularParams = Object.entries(params.getParameters()).map(([k, v]) => (
      Object.hasOwn(regularRawParams, k) ? regularRawParams[k] : `"${k}=${v}"`
    )).join(", ")
    const parts = [groupedParams, regularParams].filter(Boolean).join(",")

    return `=${FORMULAS.LOAD_FC}(${parts})`
  }

  const isCurveFormula = formula => formula?.includes(FORMULAS.LOAD_FC) || formula?.includes(FORMULAS.LOAD_FC.replaceAll(".", "_"))

  const extractParamsFromFormula = formula => {
    const argsString = formula.slice(formula.indexOf("(") + 1, formula.lastIndexOf(")")).replace(/\\"/g, '"')
    const rawParams = splitFormulaArguments(argsString)
    const params = rawParams.map(normalizeFormulaArgument)

    Object.defineProperty(params, "rawParams", {
      value: rawParams,
      enumerable: false,
      configurable: true,
      writable: true
    })
    return params
  }

  const validateTable = async ({ item, cellValue, cell, formula, returnItem = false }) => {
    console.info("Validating formula:", formula)
    if((!cellValue || String(cellValue).startsWith(FC_PARAMS_PREFIX)) && isCurveFormula(formula)) {
      const parsedFormula = await ExcelUtils.parseFormula({
        formula,
        address: cell.address,
        formulaType: FORMULAS.LOAD_FC,
        extractParamsFromFormula
      })
      const parsedParameters = new Parameters.Builder()
        .withUserParameters(parsedFormula)
        .withGrouping(["groupName", "column", "product", "root", "source"])
        .build();
      const toReturn = {
        parsedParameters,
        formula,
        prefix: FC_PARAMS_PREFIX,
        address: cell.address
      }
      if(returnItem) {
        return toReturn;
      }
      return item.concat(toReturn)
    }
    return item
  }

  return {
    getForwardCurve,
    getForwardCurveDatapoint,
    extractParamsFromFormula,
    isCurveFormula,
    FC_PARAMS_PREFIX,
    paramsToFormula,
    validateTable,
    PLAIN_PARSED_CURVE_PARAMS,
    DEFAULT_CSV_HEADERS,
    SYMBOL,
    PRODUCT,
    ROOT,
    DATA_TYPE,
    CC_ROOT,
    CSV_HEADER_OPTIONS,
    getBody
  }
})()
